import { FormControl, FormHelperText, MenuItem, Select } from "@mui/material";
import type { KeyboardEvent, WheelEvent } from "react";

import type { VariableDataType } from "@/components/rule-canvas-builder/hooks/useRuleVariables";

import CoAppTextField from "@/components/global/components/inputs/CoAppTextField";

interface CollectionEntryValueInputProps {
	dataType: VariableDataType;
	value: string;
	onChange: (value: string) => void;
	error?: boolean;
	helperText?: string;
	/** Keeps helper line height when helperText is empty (avoids row height jump). */
	reserveHelperSpace?: boolean;
}

function resolvedHelperText(helperText?: string, reserveHelperSpace?: boolean): string | undefined {
	if (helperText) return helperText;
	if (reserveHelperSpace) return "\u00a0";
	return undefined;
}

/** DataGrid steals Space/arrows; keep keys in the cell editor. */
function keepKeyboardInCell(event: KeyboardEvent) {
	event.stopPropagation();
}

/** Wheel on focused number inputs increments value; blur instead. */
function preventScrollChangingNumber(event: WheelEvent<HTMLInputElement>) {
	event.currentTarget.blur();
}

const numberInputSx = {
	"& input[type=number]": {
		MozAppearance: "textfield",
	},
	"& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
		WebkitAppearance: "none",
		margin: 0,
	},
};

export default function CollectionEntryValueInput({
	dataType,
	value,
	onChange,
	error = false,
	helperText,
	reserveHelperSpace = false,
}: CollectionEntryValueInputProps) {
	const displayHelper = resolvedHelperText(helperText, reserveHelperSpace);

	switch (dataType) {
		case "boolean":
			return (
				<FormControl error={error} sx={{ width: "100%" }}>
					<Select
						size="small"
						value={value === "" ? "" : value}
						displayEmpty
						onChange={(e) => onChange(String(e.target.value))}
						onKeyDown={keepKeyboardInCell}
					>
						<MenuItem value="">
							<em>Select…</em>
						</MenuItem>
						<MenuItem value="true">True</MenuItem>
						<MenuItem value="false">False</MenuItem>
					</Select>
					{displayHelper != null ? (
						<FormHelperText sx={{ visibility: helperText ? "visible" : "hidden" }}>
							{displayHelper}
						</FormHelperText>
					) : null}
				</FormControl>
			);
		case "number":
			return (
				<CoAppTextField
					size="small"
					fullWidth
					margin="none"
					type="number"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={keepKeyboardInCell}
					slotProps={{ htmlInput: { onWheel: preventScrollChangingNumber } }}
					sx={numberInputSx}
					error={error}
					helperText={displayHelper}
					FormHelperTextProps={{ sx: { color: error ? "#DE3730" : undefined } }}
				/>
			);
		case "decimal":
			return (
				<CoAppTextField
					size="small"
					fullWidth
					margin="none"
					type="number"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={keepKeyboardInCell}
					slotProps={{
						htmlInput: { step: "any", onWheel: preventScrollChangingNumber },
					}}
					sx={numberInputSx}
					error={error}
					helperText={displayHelper}
					FormHelperTextProps={{ sx: { color: error ? "#DE3730" : undefined } }}
				/>
			);
		case "date":
			return (
				<CoAppTextField
					size="small"
					fullWidth
					margin="none"
					type="date"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={keepKeyboardInCell}
					error={error}
					helperText={displayHelper}
					slotProps={{ inputLabel: { shrink: true } }}
					FormHelperTextProps={{ sx: { color: error ? "#DE3730" : undefined } }}
				/>
			);
		case "datetime":
			return (
				<CoAppTextField
					size="small"
					fullWidth
					margin="none"
					type="datetime-local"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={keepKeyboardInCell}
					error={error}
					helperText={displayHelper}
					slotProps={{ inputLabel: { shrink: true } }}
					FormHelperTextProps={{ sx: { color: error ? "#DE3730" : undefined } }}
				/>
			);
		case "time":
			return (
				<CoAppTextField
					size="small"
					fullWidth
					margin="none"
					type="time"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={keepKeyboardInCell}
					error={error}
					helperText={displayHelper}
					slotProps={{ inputLabel: { shrink: true } }}
					FormHelperTextProps={{ sx: { color: error ? "#DE3730" : undefined } }}
				/>
			);
		default:
			return (
				<CoAppTextField
					size="small"
					fullWidth
					margin="none"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={keepKeyboardInCell}
					error={error}
					helperText={displayHelper}
					FormHelperTextProps={{ sx: { color: error ? "#DE3730" : undefined } }}
				/>
			);
	}
}
